import React, { useMemo, useState } from 'react';
import { useOnTimeLiveData } from '../hooks/useOnTimeLiveData';
import { asDate, formatMoney } from '../lib/formatters';
import { filterActiveOperationalOrders, isClosedOperationalOrder, isDemoOrGhostOrder } from '../lib/liveOrderFilters';

function safeArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function firstText(...values) {
  return values.map((value) => String(value || '').trim()).find(Boolean) || '';
}

function stageKey(order = {}) {
  return String(order.stage || order.status || order.storeStatus || 'new').toLowerCase();
}

function stageLabel(order = {}) {
  const stage = stageKey(order);
  if (['new', 'awaiting_store', 'waiting'].includes(stage)) return 'Waiting';
  if (['packing', 'store_packing', 'preparing'].includes(stage)) return 'Packing';
  if (['ready', 'ready_for_driver', 'packed'].includes(stage)) return 'Ready';
  if (['collected', 'picked_up'].includes(stage)) return 'Collected';
  if (['out_for_delivery', 'on_route'].includes(stage)) return 'On the way';
  if (['delivered', 'completed'].includes(stage)) return 'Delivered';
  if (['cancelled', 'canceled'].includes(stage)) return 'Cancelled';
  return stage ? stage.replace(/_/g, ' ') : 'Waiting';
}

function stageTone(order = {}) {
  const label = stageLabel(order).toLowerCase();
  if (label.includes('waiting')) return 'text-pink-100 border-pink-400/30 bg-pink-500/15';
  if (label.includes('packing')) return 'text-amber-100 border-amber-400/30 bg-amber-500/15';
  if (label.includes('ready')) return 'text-cyan-100 border-cyan-400/30 bg-cyan-500/15';
  if (label.includes('way') || label.includes('route')) return 'text-blue-100 border-blue-400/30 bg-blue-500/15';
  return 'text-emerald-100 border-emerald-400/30 bg-emerald-500/15';
}

function orderId(order = {}) {
  return firstText(order.orderNumber, order.orderNo, order.displayId, order.id, 'Order');
}

function customerName(order = {}) {
  const raw = order.raw || {};
  return firstText(order.customerName, order.customer?.name, raw.customerName, raw.customer?.name, 'Customer');
}

function customerPhone(order = {}) {
  const raw = order.raw || {};
  return firstText(order.customerPhone, order.customer?.phone, raw.customerPhone, raw.customer?.phone, 'No phone');
}

function storeName(order = {}) {
  const raw = order.raw || {};
  return firstText(order.store, order.storeName, order.assignedStore, order.assignment?.storeName, raw.store, raw.storeName, 'Unassigned');
}

function driverName(order = {}) {
  return firstText(order.assignedDriverName, order.assignedDriver, order.driverName, order.assignment?.driverName, 'Unassigned');
}

function money(value) {
  return formatMoney(Number(value || 0));
}

function orderTotal(order = {}) {
  const raw = order.raw || {};
  const direct = Number(order.total ?? order.orderValue ?? raw.total ?? raw.orderValue ?? raw.pricing?.total ?? 0);
  if (Number.isFinite(direct) && direct > 0) return direct;
  return safeArray(order.items || raw.items).reduce((sum, item) => {
    const qty = Number(item.qty ?? item.quantity ?? 1) || 1;
    const price = Number(item.price ?? item.unitPrice ?? item.sellPrice ?? item.total ?? 0) || 0;
    return sum + (price * qty);
  }, 0);
}

function orderItems(order = {}) {
  const raw = order.raw || {};
  return safeArray(order.items).length ? safeArray(order.items) : safeArray(raw.items);
}

function orderAge(order = {}) {
  const raw = order.raw || {};
  const timings = order.timings || raw.timings || {};
  return asDate(order.createdAt || order.placedAt || timings.createdAt || raw.createdAt || raw.placedAt || order.updatedAt || raw.updatedAt);
}

function minutesSince(date) {
  const d = asDate(date);
  if (!d) return 0;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
}

function fmtMins(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function riskLabel(order = {}) {
  const mins = minutesSince(orderAge(order));
  if (mins >= 45 || order.issueFlag || order.raw?.issueFlag) return 'At Risk';
  if (mins >= 25) return 'Attention';
  return 'On Track';
}

function riskTone(label = '') {
  if (label === 'At Risk') return 'text-rose-200 border-rose-400/30 bg-rose-500/15';
  if (label === 'Attention') return 'text-amber-200 border-amber-400/30 bg-amber-500/15';
  return 'text-emerald-200 border-emerald-400/30 bg-emerald-500/15';
}

const card = 'rounded-3xl border border-cyan-300/15 bg-[#061222]/75 shadow-[0_0_28px_rgba(0,200,255,.08)]';
const pill = 'rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-left text-sm font-extrabold text-white transition hover:border-cyan-300/40';
const button = 'rounded-2xl border border-cyan-300/20 bg-cyan-400/10 px-4 py-3 text-sm font-extrabold text-cyan-100 transition hover:bg-cyan-400/20';

export default function OnTimeOrdersPage() {
  const { orders = [], loading } = useOnTimeLiveData();
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');

  const activeOrders = useMemo(() => filterActiveOperationalOrders(orders), [orders]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return activeOrders
      .filter((order) => {
        if (filter === 'all') return true;
        const label = stageLabel(order).toLowerCase();
        if (filter === 'waiting') return label.includes('waiting');
        if (filter === 'packing') return label.includes('packing');
        if (filter === 'ready') return label.includes('ready');
        if (filter === 'route') return label.includes('way') || label.includes('route') || label.includes('collected');
        if (filter === 'risk') return riskLabel(order) === 'At Risk';
        return true;
      })
      .filter((order) => {
        if (!q) return true;
        return [orderId(order), customerName(order), customerPhone(order), storeName(order), driverName(order), stageLabel(order)]
          .join(' ')
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => minutesSince(orderAge(b)) - minutesSince(orderAge(a)));
  }, [activeOrders, filter, query]);

  const selectedOrder = filtered.find((order) => order.id === selectedId) || filtered[0] || null;
  const closedCount = safeArray(orders).filter((order) => isClosedOperationalOrder(order) && !isDemoOrGhostOrder(order)).length;

  const counts = {
    all: activeOrders.length,
    waiting: activeOrders.filter((order) => stageLabel(order).toLowerCase().includes('waiting')).length,
    packing: activeOrders.filter((order) => stageLabel(order).toLowerCase().includes('packing')).length,
    ready: activeOrders.filter((order) => stageLabel(order).toLowerCase().includes('ready')).length,
    route: activeOrders.filter((order) => {
      const label = stageLabel(order).toLowerCase();
      return label.includes('way') || label.includes('route') || label.includes('collected');
    }).length,
    risk: activeOrders.filter((order) => riskLabel(order) === 'At Risk').length,
  };

  return (
    <div className="min-h-screen px-6 py-8 text-white md:px-10">
      <section className="mb-8 flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="mb-3 text-xs font-black uppercase tracking-[0.45em] text-cyan-300">Control Tower</div>
          <h1 className="text-4xl font-black md:text-5xl">Orders</h1>
          <p className="mt-3 max-w-3xl text-base text-slate-300">
            Active live orders only. Completed and cancelled orders are kept out of this workspace and belong in Archives.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div className={card + ' p-5'}><div className="text-sm text-slate-300">Active Orders</div><div className="mt-2 text-3xl font-black">{counts.all}</div></div>
          <div className={card + ' p-5'}><div className="text-sm text-slate-300">At Risk</div><div className="mt-2 text-3xl font-black">{counts.risk}</div></div>
          <div className={card + ' p-5'}><div className="text-sm text-slate-300">Ready</div><div className="mt-2 text-3xl font-black">{counts.ready}</div></div>
          <div className={card + ' p-5'}><div className="text-sm text-slate-300">In Archives</div><div className="mt-2 text-3xl font-black">{closedCount}</div></div>
        </div>
      </section>

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className={card + ' overflow-hidden'}>
          <div className="flex flex-col gap-3 border-b border-white/10 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {[
                ['all', 'All Active', counts.all],
                ['waiting', 'Waiting', counts.waiting],
                ['packing', 'Packing', counts.packing],
                ['ready', 'Ready', counts.ready],
                ['route', 'Route', counts.route],
                ['risk', 'At Risk', counts.risk],
              ].map(([key, label, count]) => (
                <button key={key} onClick={() => setFilter(key)} className={`${pill} ${filter === key ? 'border-cyan-300/50 bg-cyan-400/15' : ''}`}>
                  {label} <span className="ml-2 rounded-lg bg-white/10 px-2 py-1 text-xs">{count}</span>
                </button>
              ))}
            </div>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search active orders..."
              className="min-w-[260px] rounded-2xl border border-white/10 bg-[#020916]/80 px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-cyan-300/50"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left">
              <thead className="bg-white/[0.03] text-xs uppercase tracking-[0.22em] text-slate-400">
                <tr>
                  <th className="px-5 py-4">Order ID</th>
                  <th className="px-5 py-4">Customer</th>
                  <th className="px-5 py-4">Store</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Driver</th>
                  <th className="px-5 py-4">Time Live</th>
                  <th className="px-5 py-4">SLA</th>
                  <th className="px-5 py-4">Total</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length ? filtered.map((order) => {
                  const risk = riskLabel(order);
                  const selected = selectedOrder?.id === order.id;
                  return (
                    <tr
                      key={order.id}
                      onClick={() => setSelectedId(order.id)}
                      className={`cursor-pointer border-t border-white/10 transition hover:bg-cyan-300/5 ${selected ? 'bg-cyan-400/10' : ''}`}
                    >
                      <td className="px-5 py-5 font-black">{orderId(order)}</td>
                      <td className="px-5 py-5"><div className="font-bold">{customerName(order)}</div><div className="text-sm text-slate-400">{customerPhone(order)}</div></td>
                      <td className="px-5 py-5"><div className="font-bold">{storeName(order)}</div><div className="text-sm text-slate-400">Live</div></td>
                      <td className="px-5 py-5"><span className={`rounded-xl border px-3 py-1 text-xs font-black ${stageTone(order)}`}>{stageLabel(order)}</span></td>
                      <td className="px-5 py-5">{driverName(order)}</td>
                      <td className="px-5 py-5"><div className="font-black">{fmtMins(minutesSince(orderAge(order)))}</div><div className="text-sm text-slate-500">active</div></td>
                      <td className="px-5 py-5"><span className={`rounded-xl border px-3 py-1 text-xs font-black ${riskTone(risk)}`}>{risk}</span></td>
                      <td className="px-5 py-5 font-bold">{money(orderTotal(order))}</td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-20 text-center">
                      <div className="mx-auto max-w-md rounded-3xl border border-cyan-300/15 bg-cyan-400/5 p-8">
                        <div className="text-2xl font-black">No active live orders</div>
                        <p className="mt-3 text-sm text-slate-300">
                          When you create or receive a new Link order, it will appear here and flow into Control Tower, Control Map, Store Link and Driver Link.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <aside className={card + ' p-5'}>
          {selectedOrder ? (
            <>
              <div className="flex items-start justify-between gap-4 border-b border-white/10 pb-5">
                <div>
                  <h2 className="text-2xl font-black">Order {orderId(selectedOrder)}</h2>
                  <p className="mt-1 text-slate-300">{storeName(selectedOrder)} · {fmtMins(minutesSince(orderAge(selectedOrder)))} live</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black">{money(orderTotal(selectedOrder))}</div>
                  <div className="text-xs text-slate-400">Order value</div>
                </div>
              </div>

              <div className="mt-5 rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                <div className="text-xs font-black uppercase tracking-[0.28em] text-cyan-300">Customer</div>
                <div className="mt-4 text-xl font-black">{customerName(selectedOrder)}</div>
                <div className="mt-1 text-slate-300">{customerPhone(selectedOrder)}</div>
                <div className="mt-4 text-sm text-slate-400">{firstText(selectedOrder.address, selectedOrder.raw?.address, 'No address captured')}</div>
              </div>

              <div className="mt-5 rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                <div className="text-xs font-black uppercase tracking-[0.28em] text-cyan-300">Order Items</div>
                <div className="mt-4 space-y-3">
                  {orderItems(selectedOrder).length ? orderItems(selectedOrder).map((item, index) => (
                    <div key={`${item.id || item.name || index}`} className="flex items-center justify-between gap-4 border-b border-white/10 pb-3 last:border-b-0 last:pb-0">
                      <div className="font-bold">{Number(item.qty ?? item.quantity ?? 1) || 1} × {firstText(item.name, item.productName, item.title, 'Item')}</div>
                      <div className="font-black">{money(Number(item.total ?? item.lineTotal ?? item.price ?? item.unitPrice ?? 0) || 0)}</div>
                    </div>
                  )) : <div className="text-sm text-slate-400">No items captured.</div>}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <button className={button}>Send Store</button>
                <button className={button}>Assign Driver</button>
                <button className={button}>View Timeline</button>
                <button className={button}>Open Tower</button>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-center">
              <div className="text-2xl font-black">Nothing active</div>
              <p className="mt-3 text-sm text-slate-300">Completed and cancelled orders are no longer shown here.</p>
            </div>
          )}
        </aside>
      </section>

      {loading && <div className="mt-4 text-sm text-slate-400">Loading live order feed...</div>}
    </div>
  );
}
